import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import OnboardingPage from "@/pages/OnboardingPage";
import { User, Globe, Users, ChevronRight, Minus, Plus, Settings as SettingsIcon, Check, X, Leaf, Sparkles, Scale, Ban, Star, Shield } from "lucide-react";
import { toLocalDateStr } from "@/lib/utils";
import { isLocked } from "@/utils/subscription";
import { LockOverlay } from "@/components/LockBadge";

const MEAL_TYPES_GUEST = ["breakfast", "lunch", "dinner", "snack"];

export default function SettingsPage() {
  const { user, api, updateUser } = useAuth();
  const { t, lang, switchLang } = useLanguage();
  const navigate = useNavigate();
  const locked = isLocked(user);
  // Гейт для маркетингових секцій: locked -> напівпрозоро + корона + клік на /subscription; інакше — вміст як є
  const gate = (node) => (locked ? <LockOverlay blur={true}>{node}</LockOverlay> : node);
  const [guestMode, setGuestMode] = useState({ enabled: false, guest_count: 0, date_from: "", date_to: "", meal_types: ["breakfast", "lunch", "dinner"] });
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user?.name || "");
  const [savingGuest, setSavingGuest] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [focusMode, setFocusMode] = useState("balance");
  const [exclusions, setExclusions] = useState([]);
  const [showAddExclusion, setShowAddExclusion] = useState(false);
  const [exclIngredient, setExclIngredient] = useState("");
  const [exclDuration, setExclDuration] = useState("2w");
  const [priorityItems, setPriorityItems] = useState([]);
  const [priorityInput, setPriorityInput] = useState("");
  const [prioritySuggestions, setPrioritySuggestions] = useState([]);
  const [consentAnalytics, setConsentAnalytics] = useState(user?.consent_analytics !== false);

  useEffect(() => {
    const fetchGuestMode = async () => {
      try {
        const res = await api().get("/guest-mode");
        setGuestMode({
          enabled: res.data?.enabled || false,
          guest_count: res.data?.guest_count || 0,
          date_from: res.data?.date_from || "",
          date_to: res.data?.date_to || "",
          meal_types: res.data?.meal_types || ["breakfast", "lunch", "dinner"],
        });
      } catch (err) { console.error(err); }
    };
    fetchGuestMode();
  }, [api]);

  useEffect(() => {
    const fetchFocusAndExclusions = async () => {
      try {
        const [fRes, eRes] = await Promise.all([
          api().get("/focus-mode"),
          api().get("/exclusions"),
        ]);
        setFocusMode(fRes.data?.mode || "balance");
        setExclusions(eRes.data || []);
      } catch (err) { console.error(err); }
    };
    fetchFocusAndExclusions();
  }, [api]);

  const handleFocusChange = async (mode) => {
    setFocusMode(mode);
    try { await api().put("/focus-mode", { mode }); } catch (err) { console.error(err); }
  };

  const handleAddExclusion = async () => {
    if (!exclIngredient.trim()) return;
    const already = exclusions.some(e => e.ingredient.toLowerCase() === exclIngredient.trim().toLowerCase());
    if (already) return;
    const now = new Date();
    const durationMap = { "1w": 7, "2w": 14, "1m": 30 };
    const days = durationMap[exclDuration] || 14;
    const until = toLocalDateStr(new Date(now.getTime() + days * 86400000));
    try {
      await api().post("/exclusions", { ingredient: exclIngredient.trim(), until_date: until });
      const res = await api().get("/exclusions");
      setExclusions(res.data || []);
      setExclIngredient("");
      setShowAddExclusion(false);
    } catch (err) { console.error(err); }
  };

  const handleRemoveExclusion = async (ingredient) => {
    try {
      await api().delete(`/exclusions/${encodeURIComponent(ingredient)}`);
      setExclusions((prev) => prev.filter((e) => e.ingredient.toLowerCase() !== ingredient.toLowerCase()));
    } catch (err) { console.error(err); }
  };

  // Priority ingredients: soft "include in every day" preference, stored in
  // user.preferences as catalog snapshots {ua,en,ru,category,icon}. Legacy plain
  // strings (shipped earlier) stay readable.
  const prodLangKey = lang === "uk" ? "ua" : lang; // product DB uses ua/en/ru
  const priorityUa = (it) => (typeof it === "string" ? it : (it.ua || ""));
  const priorityLabel = (it) =>
    typeof it === "string" ? it : (it[prodLangKey] || it.ua || "");
  const priorityIcon = (it) => (typeof it === "string" ? "📦" : (it.icon || "📦"));

  useEffect(() => {
    setPriorityItems(user?.preferences?.priority_ingredients || []);
  }, [user]);

  // Catalog autocomplete (same pattern as FridgePage add-item search).
  useEffect(() => {
    const q = priorityInput.trim();
    if (q.length < 2) { setPrioritySuggestions([]); return; }
    const search = async () => {
      try {
        const res = await api().get(`/products/search?query=${encodeURIComponent(q)}&limit=8`);
        setPrioritySuggestions(res.data?.results || []);
      } catch { setPrioritySuggestions([]); }
    };
    const debounce = setTimeout(search, 200);
    return () => clearTimeout(debounce);
  }, [priorityInput, api]);

  const savePriority = async (list) => {
    try {
      const res = await api().put("/preferences", { priority_ingredients: list });
      const saved = res.data?.preferences?.priority_ingredients || [];
      setPriorityItems(saved);
      updateUser({ preferences: res.data?.preferences });
    } catch (err) { console.error(err); }
  };

  // Catalog-only: resolve the picked suggestion to its canonical record via lookup.
  const handleSelectPriority = async (s) => {
    if (priorityItems.length >= 10) return;
    let obj;
    try {
      const res = await api().get(`/products/lookup?name=${encodeURIComponent(s.name)}`);
      const r = res.data || {};
      obj = {
        ua: r.ua || r.name_matched || s.name,
        en: r.en || "",
        ru: r.ru || "",
        category: r.category || s.category || "",
        icon: r.icon || s.icon || "",
      };
    } catch {
      obj = { ua: s.name, en: "", ru: "", category: s.category || "", icon: s.icon || "" };
    }
    setPriorityInput("");
    setPrioritySuggestions([]);
    if (priorityItems.some((p) => priorityUa(p).toLowerCase() === obj.ua.toLowerCase())) return;
    await savePriority([...priorityItems, obj]);
  };

  const handleRemovePriority = async (ua) => {
    await savePriority(priorityItems.filter((p) => priorityUa(p).toLowerCase() !== ua.toLowerCase()));
  };

  const handleNameSave = async () => {
    try {
      const res = await api().put("/auth/profile", { name: nameInput });
      updateUser({ name: res.data.name });
      setEditingName(false);
    } catch (err) { console.error(err); }
  };

  const handleLanguageChange = async (newLang) => {
    switchLang(newLang);
    // Update user in auth context so all components see new language
    updateUser({ language: newLang });
    try { await api().put("/auth/profile", { language: newLang }); } catch (err) { console.error(err); }
  };

  const saveGuestMode = async (newMode) => {
    setSavingGuest(true);
    setGuestMode(newMode);
    try {
      await api().put("/guest-mode", newMode);
    } catch (err) { console.error(err); }
    setSavingGuest(false);
  };

  const handleGuestModeToggle = () => {
    const newMode = { ...guestMode, enabled: !guestMode.enabled, guest_count: guestMode.enabled ? 0 : 2 };
    saveGuestMode(newMode);
  };

  const handleGuestCountChange = (delta) => {
    const newCount = Math.max(0, Math.min(20, guestMode.guest_count + delta));
    saveGuestMode({ ...guestMode, guest_count: newCount, enabled: newCount > 0 });
  };

  const toggleGuestMealType = (meal) => {
    const newMeals = guestMode.meal_types.includes(meal)
      ? guestMode.meal_types.filter((m) => m !== meal)
      : [...guestMode.meal_types, meal];
    saveGuestMode({ ...guestMode, meal_types: newMeals });
  };

  const handleConsentToggle = async () => {
    const next = !consentAnalytics;
    setConsentAnalytics(next);           // optimistic
    try {
      await api().post("/consent/analytics", { granted: next });
      updateUser({ consent_analytics: next });
    } catch (err) {
      console.error(err);
      setConsentAnalytics(!next);        // revert on failure
    }
  };

  if (showOnboarding) {
    return <OnboardingPage isRedoing={true} onComplete={() => setShowOnboarding(false)} />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-32" data-testid="settings-page">
      <div className="pl-[72px] pr-5 pt-6 pb-4">
        <h1 className="font-heading text-3xl text-[#3D4035]">{t("settingsTitle")}</h1>
      </div>

      <div className="px-5 space-y-4">
        {/* Profile */}
        <div className="bg-white rounded-2xl p-5 border border-black/5" data-testid="settings-profile">
          <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-4">{t("profile")}</h3>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#7B9E7F] flex items-center justify-center">
              <User className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              {editingName ? (
                <div className="flex gap-2">
                  <input data-testid="settings-name-input" type="text" value={nameInput} onChange={(e) => setNameInput(e.target.value)}
                    maxLength={50}
                    className="flex-1 bg-[#FAFAF8] ring-1 ring-[#E8E5E0] rounded-xl h-10 px-3 font-body text-[#3D4035] outline-none text-sm" autoFocus />
                  <button data-testid="settings-name-save" onClick={handleNameSave}
                    className="bg-[#7B9E7F] text-white rounded-xl px-4 h-10 font-body text-sm font-semibold">{t("save")}</button>
                </div>
              ) : (
                <button data-testid="settings-edit-name" onClick={() => setEditingName(true)} className="text-left">
                  <p className="font-body font-semibold text-[#3D4035]">{user?.name}</p>
                  <p className="font-body text-sm text-[#A5A58D]">{user?.email}</p>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Language */}
        <div className="bg-white rounded-2xl p-5 border border-black/5" data-testid="settings-language">
          <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-4">
            <Globe className="w-4 h-4 inline-block mr-1.5" strokeWidth={1.5} />{t("language")}
          </h3>
          <div className="flex gap-2">
            <button data-testid="settings-lang-uk" onClick={() => handleLanguageChange("uk")}
              className={`flex-1 py-3 rounded-xl font-body font-semibold transition-all ${lang === "uk" ? "bg-[#7B9E7F] text-white" : "bg-[#EBE9E4] text-[#6B705C] hover:bg-[#E0DDD7]"}`}>
              🇺🇦 UA
            </button>
            <button data-testid="settings-lang-en" onClick={() => handleLanguageChange("en")}
              className={`flex-1 py-3 rounded-xl font-body font-semibold transition-all ${lang === "en" ? "bg-[#7B9E7F] text-white" : "bg-[#EBE9E4] text-[#6B705C] hover:bg-[#E0DDD7]"}`}>
              🇬🇧 EN
            </button>
            <button data-testid="settings-lang-ru" onClick={() => handleLanguageChange("ru")}
              className={`flex-1 py-3 rounded-xl font-body font-semibold transition-all ${lang === "ru" ? "bg-[#7B9E7F] text-white" : "bg-[#EBE9E4] text-[#6B705C] hover:bg-[#E0DDD7]"}`}>
              🇷🇺 RU
            </button>
          </div>
        </div>

        {/* Guest Mode */}
        <div className="bg-white rounded-2xl p-5 border border-black/5" data-testid="settings-guest-mode">
          <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2">
            <Users className="w-4 h-4 inline-block mr-1.5" strokeWidth={1.5} />{t("guestMode")}
          </h3>
          <p className="font-body text-sm text-[#A5A58D] mb-4">{t("guestModeDesc")}</p>

          <div className="flex items-center justify-between mb-4">
            <button data-testid="guest-mode-toggle" onClick={handleGuestModeToggle}
              className={`relative w-14 h-8 rounded-full transition-colors ${guestMode.enabled ? "bg-[#7B9E7F]" : "bg-[#E8E5E0]"}`}>
              <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${guestMode.enabled ? "translate-x-7" : "translate-x-1"}`} />
            </button>
            {guestMode.enabled && (
              <div className="flex items-center gap-2">
                <button data-testid="guest-count-decrease" onClick={() => handleGuestCountChange(-1)}
                  className="w-8 h-8 rounded-full bg-[#EBE9E4] flex items-center justify-center">
                  <Minus className="w-3.5 h-3.5 text-[#6B705C]" strokeWidth={2} />
                </button>
                <span className="font-body font-bold text-[#3D4035] text-lg w-6 text-center" data-testid="guest-count-value">{guestMode.guest_count}</span>
                <button data-testid="guest-count-increase" onClick={() => handleGuestCountChange(1)}
                  className="w-8 h-8 rounded-full bg-[#EBE9E4] flex items-center justify-center">
                  <Plus className="w-3.5 h-3.5 text-[#6B705C]" strokeWidth={2} />
                </button>
                <span className="font-body text-sm text-[#A5A58D]">{t("guests")}</span>
              </div>
            )}
          </div>

          {guestMode.enabled && (
            <div className="space-y-3 pt-3 border-t border-[#EBE9E4]">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-1 block">{t("dateFrom")}</label>
                  <input data-testid="guest-date-from" type="date" value={guestMode.date_from || ""}
                    onChange={(e) => saveGuestMode({ ...guestMode, date_from: e.target.value })}
                    className="w-full bg-[#FAFAF8] ring-1 ring-[#E8E5E0] rounded-xl h-11 px-3 font-body text-sm text-[#3D4035] outline-none" />
                </div>
                <div>
                  <label className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-1 block">{t("dateTo")}</label>
                  <input data-testid="guest-date-to" type="date" value={guestMode.date_to || ""}
                    onChange={(e) => saveGuestMode({ ...guestMode, date_to: e.target.value })}
                    className="w-full bg-[#FAFAF8] ring-1 ring-[#E8E5E0] rounded-xl h-11 px-3 font-body text-sm text-[#3D4035] outline-none" />
                </div>
              </div>
              <div>
                <label className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2 block">{t("mealsForGuests")}</label>
                <div className="flex flex-wrap gap-2">
                  {MEAL_TYPES_GUEST.map((meal) => (
                    <button key={meal} onClick={() => toggleGuestMealType(meal)}
                      className={`px-3 py-2 rounded-full font-body text-xs font-semibold transition-all ${
                        guestMode.meal_types.includes(meal) ? "bg-[#7B9E7F] text-white" : "bg-[#EBE9E4] text-[#6B705C]"
                      }`}>
                      {t(meal)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Privacy */}
        <div className="bg-white rounded-2xl p-5 border border-black/5" data-testid="settings-privacy">
          <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2">
            <Shield className="w-4 h-4 inline-block mr-1.5" strokeWidth={1.5} />{t("privacyTitle")}
          </h3>
          <p className="font-body text-sm text-[#A5A58D] mb-4">{t("privacyAnalyticsDesc")}</p>

          <div className="flex items-center justify-between">
            <button data-testid="consent-analytics-toggle" onClick={handleConsentToggle}
              className={`relative w-14 h-8 rounded-full transition-colors ${consentAnalytics ? "bg-[#7B9E7F]" : "bg-[#E8E5E0]"}`}>
              <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${consentAnalytics ? "translate-x-7" : "translate-x-1"}`} />
            </button>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-body text-sm text-[#7B9E7F] underline">
              {t("privacy")}
            </a>
          </div>
        </div>

        {/* Focus Mode */}
        <div className="bg-white rounded-2xl p-5 border border-black/5" data-testid="settings-focus-mode">
          <div className="flex items-center gap-2 mb-1">
            <Scale className="w-4 h-4 text-[#7B9E7F]" strokeWidth={1.5} />
            <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold">{t("focusMode")}</h3>
          </div>
          <p className="font-body text-xs text-[#A5A58D] mb-4">{t("focusModeDesc")}</p>
          {gate(
          <div className="space-y-2">
            {[
              { key: "no_waste", icon: Leaf, label: t("focusNoWaste"), desc: t("focusNoWasteDesc"), color: "#7B9E7F" },
              { key: "new_flavors", icon: Sparkles, label: t("focusNewFlavors"), desc: t("focusNewFlavorsDesc"), color: "#E9C46A" },
              { key: "balance", icon: Scale, label: t("focusBalance"), desc: t("focusBalanceDesc"), color: "#6B705C" },
            ].map(({ key, icon: Icon, label, desc, color }) => (
              <button key={key} data-testid={`focus-${key}`}
                onClick={() => handleFocusChange(key)}
                className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-center gap-3 ${
                  focusMode === key
                    ? "border-[#7B9E7F] bg-[#7B9E7F]/5 shadow-sm"
                    : "border-[#E8E5E0] hover:border-[#7B9E7F]/30"
                }`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0`} style={{ backgroundColor: focusMode === key ? color + "20" : "#F5F3F0" }}>
                  <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body font-semibold text-sm text-[#3D4035]">{label}</p>
                  <p className="font-body text-xs text-[#A5A58D]">{desc}</p>
                </div>
                {focusMode === key && (
                  <div className="w-5 h-5 rounded-full bg-[#7B9E7F] flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-white" strokeWidth={2} />
                  </div>
                )}
              </button>
            ))}
          </div>
          )}
        </div>

        {/* Temporary Exclusions */}
        <div className="bg-white rounded-2xl p-5 border border-black/5" data-testid="settings-exclusions">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4 text-[#BC4749]" strokeWidth={1.5} />
              <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold">{t("temporaryExclusions")}</h3>
            </div>
            <button data-testid="add-exclusion-btn" onClick={locked ? () => navigate("/subscription") : () => setShowAddExclusion(!showAddExclusion)}
              className="w-7 h-7 rounded-full bg-[#7B9E7F] text-white flex items-center justify-center">
              <Plus className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          <p className="font-body text-xs text-[#A5A58D] mb-3">{t("temporaryExclusionsDesc")}</p>

          {gate(<>
          {showAddExclusion && (
            <div className="mb-3 p-3 bg-[#FAFAF8] rounded-xl border border-[#E8E5E0] space-y-2">
              <input data-testid="exclusion-input" type="text" value={exclIngredient}
                onChange={(e) => setExclIngredient(e.target.value)}
                placeholder={t("exclusionIngredient")}
                className="w-full bg-white ring-1 ring-[#E8E5E0] rounded-lg h-10 px-3 font-body text-sm text-[#3D4035] outline-none" autoFocus />
              <div className="flex gap-2">
                {[
                  { key: "1w", label: t("oneWeek") },
                  { key: "2w", label: t("twoWeeks") },
                  { key: "1m", label: t("oneMonth") },
                ].map(({ key, label }) => (
                  <button key={key} data-testid={`excl-dur-${key}`}
                    onClick={() => setExclDuration(key)}
                    className={`flex-1 h-9 rounded-lg font-body text-xs font-semibold transition-all ${
                      exclDuration === key ? "bg-[#7B9E7F] text-white" : "bg-white border border-[#E8E5E0] text-[#6B705C]"
                    }`}>{label}</button>
                ))}
              </div>
              <button data-testid="save-exclusion" onClick={handleAddExclusion} disabled={!exclIngredient.trim()}
                className="w-full h-10 rounded-lg bg-[#7B9E7F] text-white font-body text-sm font-semibold disabled:opacity-50">
                {t("addExclusion")}
              </button>
            </div>
          )}

          {exclusions.length === 0 ? (
            <p className="font-body text-sm text-[#A5A58D] text-center py-2">{t("noExclusions")}</p>
          ) : (
            <div className="space-y-2">
              {exclusions.map((e, i) => (
                <div key={i} data-testid={`exclusion-${i}`}
                  className="flex items-center justify-between p-3 bg-[#BC4749]/5 rounded-xl border border-[#BC4749]/10">
                  <div>
                    <p className="font-body font-semibold text-sm text-[#3D4035]">{e.ingredient}</p>
                    <p className="font-body text-xs text-[#A5A58D]">{t("excludeUntil")} {e.until_date}</p>
                  </div>
                  <button onClick={() => handleRemoveExclusion(e.ingredient)}
                    className="w-7 h-7 rounded-full bg-[#BC4749]/10 flex items-center justify-center hover:bg-[#BC4749]/20 transition-colors">
                    <X className="w-3.5 h-3.5 text-[#BC4749]" strokeWidth={2} />
                  </button>
                </div>
              ))}
            </div>
          )}
          </>)}
        </div>

        {/* Priority Ingredients */}
        <div className="bg-white rounded-2xl p-5 border border-black/5" data-testid="settings-priority">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-4 h-4 text-[#7B9E7F]" strokeWidth={1.5} />
            <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold">{t("priorityIngredients")}</h3>
          </div>
          <p className="font-body text-xs text-[#A5A58D] mb-3">{t("priorityIngredientsDesc")}</p>

          {gate(<>
          {priorityItems.length < 10 && (
            <div className="relative mb-3">
              <input data-testid="priority-input" type="text" value={priorityInput}
                onChange={(e) => setPriorityInput(e.target.value)}
                placeholder={t("priorityIngredient")}
                className="w-full bg-white ring-1 ring-[#E8E5E0] rounded-lg h-10 px-3 font-body text-sm text-[#3D4035] outline-none" />
              {prioritySuggestions.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-xl border border-black/5 shadow-lg divide-y divide-[#F2E9E4] max-h-60 overflow-y-auto">
                  {prioritySuggestions.map((s, idx) => (
                    <button key={idx} data-testid={`priority-suggestion-${idx}`}
                      onClick={() => handleSelectPriority(s)}
                      className="w-full px-4 py-2.5 text-left font-body text-sm text-[#3D4035] hover:bg-[#FAFAF8] transition-colors flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <span className="text-base">{s.icon || "📦"}</span>
                        <span>{s.name}</span>
                      </span>
                      <span className="font-body text-xs text-[#A5A58D]">{t(`cat_${s.category}`)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {priorityItems.length >= 10 && (
            <p className="font-body text-xs text-[#BC4749] mb-2">{t("priorityLimitReached")}</p>
          )}

          {priorityItems.length === 0 ? (
            <p className="font-body text-sm text-[#A5A58D] text-center py-2">{t("noPriority")}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {priorityItems.map((p, i) => (
                <span key={i} data-testid={`priority-${i}`}
                  className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-[#7B9E7F]/10 rounded-full border border-[#7B9E7F]/20">
                  <span className="text-base">{priorityIcon(p)}</span>
                  <span className="font-body text-sm font-semibold text-[#3D4035]">{priorityLabel(p)}</span>
                  <button onClick={() => handleRemovePriority(priorityUa(p))}
                    className="w-5 h-5 rounded-full bg-[#7B9E7F]/15 flex items-center justify-center hover:bg-[#7B9E7F]/30 transition-colors">
                    <X className="w-3 h-3 text-[#6B705C]" strokeWidth={2} />
                  </button>
                </span>
              ))}
            </div>
          )}
          </>)}
        </div>

        {/* Redo Onboarding */}
        <button data-testid="redo-onboarding-button" onClick={() => setShowOnboarding(true)}
          className="w-full bg-white rounded-2xl p-5 border border-black/5 flex items-center justify-between hover:bg-[#FAFAF8] transition-colors">
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-5 h-5 text-[#7B9E7F]" strokeWidth={1.5} />
            <span className="font-body font-semibold text-[#3D4035]">{t("redoOnboarding")}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#A5A58D]" strokeWidth={1.5} />
        </button>

        {/* Version */}
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between">
            <span className="font-body text-[#6B705C]">{t("appVersion")}</span>
            <span className="font-body text-sm text-[#A5A58D]">1.0.0</span>
          </div>
        </div>
      </div>
    </div>
  );
}
